import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import {
  Activity, Backpack, Building2, Bus, Camera, FileCheck2, HeartPulse,
  Home, Package, PhoneCall, Plus, Save, ShieldCheck, SlidersHorizontal, Star,
  UserRound, Utensils, X,
} from "lucide-react";
import { toast } from "sonner";
import { LoadingState, EmptyState, ErrorState, PermissionDeniedState } from "@/components/States";
import { Btn, Field, inputCls } from "@/components/Primitives";
import { SummitFormActions, SummitFormHeader, SummitFormSection } from "@/components/forms/SummitForm";
import { useAuth } from "@/context/AuthContext";
import { api, confirmAction, formatApiError, formatApiErrorDetail } from "@/lib/api";

const categories = [
  "accommodation", "transport", "equipment rental", "catering", "local guide",
  "permit provider", "medical support", "photographer", "activity provider",
  "porter", "insurance", "other",
];
const categoryMeta = {
  accommodation: { icon: Home, label: "Stay", badge: "bg-[#E7F2ED] text-[#2D7A65] ring-[#BFD5C6]", pulse: "bg-[#2D7A65]" },
  transport: { icon: Bus, label: "Move", badge: "bg-[#EAF2FF] text-[#315D8A] ring-[#BED1EA]", pulse: "bg-[#315D8A]" },
  "equipment rental": { icon: Package, label: "Gear", badge: "bg-[#F7F1E5] text-[#7A5D2D] ring-[#E6D7B9]", pulse: "bg-[#C8962D]" },
  catering: { icon: Utensils, label: "Food", badge: "bg-[#FFF1E7] text-[#A0522D] ring-[#F0C9B1]", pulse: "bg-[#D96B35]" },
  "local guide": { icon: UserRound, label: "Guide", badge: "bg-[#E8F5EE] text-[#236D5C] ring-[#B7DEC9]", pulse: "bg-[#2D7A65]" },
  "permit provider": { icon: FileCheck2, label: "Permit", badge: "bg-[#F0EDFF] text-[#5C4D9A] ring-[#D7CFF5]", pulse: "bg-[#6C5BAA]" },
  "medical support": { icon: HeartPulse, label: "Care", badge: "bg-[#FEEAEC] text-[#A33B4B] ring-[#F3C2CA]", pulse: "bg-[#D95366]" },
  photographer: { icon: Camera, label: "Media", badge: "bg-[#E7F7F8] text-[#24717B] ring-[#BEE1E4]", pulse: "bg-[#2A8C99]" },
  "activity provider": { icon: Activity, label: "Activity", badge: "bg-[#EDF6E8] text-[#4F752B] ring-[#CBE0BC]", pulse: "bg-[#6E9E3B]" },
  porter: { icon: Backpack, label: "Porter", badge: "bg-[#F2EFE8] text-[#65513B] ring-[#DBD0BE]", pulse: "bg-[#8A6A48]" },
  insurance: { icon: ShieldCheck, label: "Cover", badge: "bg-[#E9F0FF] text-[#415E9B] ring-[#C6D5F5]", pulse: "bg-[#526EB0]" },
  other: { icon: Building2, label: "Vendor", badge: "bg-[#EEF1EF] text-[#516259] ring-[#D5DDD7]", pulse: "bg-[#516259]" },
};

const blankVendor = {
  category: "other", name: "", region: "", service_types: "", website: "", address: "",
  latitude: "", longitude: "", contact_name: "", contact_phone: "", contact_email: "",
  capacity: "", charges: "", details: "", pricing_notes: "", internal_notes: "", rating: "", tags: "",
  active: true,
};

function csv(value) { return Array.isArray(value) ? value.join(", ") : value || ""; }
function list(value) { return String(value || "").split(",").map((item) => item.trim()).filter(Boolean); }
function freshVendor() { return { ...blankVendor }; }
function numericValue(value) { return value === undefined || value === null || value === "" ? null : Number(value); }
function phoneHref(value) { return `tel:${String(value || "").replace(/[^\d+]/g, "")}`; }
function vendorCategoryMeta(category) { return categoryMeta[String(category || "").toLowerCase()] || categoryMeta.other; }
function vendorSearchText(vendor) {
  return [
    vendor.name, vendor.category, vendor.region, vendor.address, vendor.service_types?.join(" "),
    vendor.details, vendor.tags?.join(" "), vendor.rating, vendor.charges, vendor.capacity,
  ].filter(Boolean).join(" ").toLowerCase();
}
function VendorCategoryBadge({ category, compact = false }) {
  const meta = vendorCategoryMeta(category);
  const Icon = meta.icon;
  return (
    <span className={`relative inline-flex shrink-0 items-center justify-center overflow-hidden rounded-2xl ring-1 ${meta.badge} ${compact ? "h-9 w-9" : "h-12 w-12"}`}>
      <span className={`absolute inset-1 rounded-2xl opacity-0 motion-safe:group-hover:animate-ping motion-safe:group-hover:opacity-25 ${meta.pulse}`} />
      <Icon className={`${compact ? "h-4 w-4" : "h-5 w-5"} relative`} />
    </span>
  );
}
function RatingStars({ value }) {
  const rating = numericValue(value);
  if (rating === null || Number.isNaN(rating)) return <span className="text-xs text-slate-500">No rating</span>;
  const rounded = Math.round(rating);
  return (
    <span className="flex items-center gap-1 text-xs font-semibold text-[#835B12]" aria-label={`Rating ${rating} out of 5`}>
      {[1, 2, 3, 4, 5].map((star) => (
        <Star key={star} className={`h-3.5 w-3.5 ${star <= rounded ? "fill-[#EBC253] text-[#EBC253]" : "text-[#D8CFBD]"}`} />
      ))}
      <span>{rating}/5</span>
    </span>
  );
}

function toForm(vendor = {}) {
  return {
    ...freshVendor(),
    ...vendor,
    service_types: csv(vendor.service_types),
    tags: csv(vendor.tags),
    latitude: vendor.latitude ?? "",
    longitude: vendor.longitude ?? "",
    capacity: vendor.capacity ?? "",
    rating: vendor.rating ?? "",
    charges: vendor.charges ?? "",
  };
}

function payload(form) {
  return {
    category: form.category,
    name: form.name.trim(),
    region: form.region,
    service_types: list(form.service_types),
    website: form.website,
    address: form.address,
    latitude: form.latitude === "" ? null : Number(form.latitude),
    longitude: form.longitude === "" ? null : Number(form.longitude),
    contact_name: form.contact_name,
    contact_phone: form.contact_phone,
    contact_email: form.contact_email,
    capacity: form.capacity === "" ? null : Number(form.capacity),
    charges: form.charges === "" ? null : Number(form.charges),
    details: form.details,
    pricing_notes: form.pricing_notes,
    internal_notes: form.internal_notes,
    rating: form.rating === "" ? null : Number(form.rating),
    tags: list(form.tags),
    active: !!form.active,
  };
}

export default function VendorMasterTab() {
  const { perms } = useAuth();
  const canRead = ["org:manage", "trip:edit"].some((p) => perms.includes(p));
  const canManage = ["org:manage", "trip:edit"].some((p) => perms.includes(p));
  const [items, setItems] = useState(null);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(freshVendor);
  const [editing, setEditing] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({
    category: "", region: "", active: "true", address: "", minCharges: "", maxCharges: "", minRating: "",
  });
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    if (!canRead) return;
    setError("");
    try {
      const params = {};
      if (filters.category) params.category = filters.category;
      if (filters.region) params.region = filters.region;
      if (filters.active !== "all") params.active = filters.active === "true";
      const { data } = await api.get("/org/vendors", { params });
      setItems(data.data || []);
    } catch (err) {
      setError(formatApiError(err, "Could not load vendors."));
    }
  }, [canRead, filters.category, filters.region, filters.active]);
  useEffect(() => { load(); }, [load]);

  const openDetail = async (vendor) => {
    setSelected(vendor);
    try {
      const { data } = await api.get(`/org/vendors/${vendor.id}`);
      setSelected(data);
    } catch (err) {
      toast.error(formatApiError(err, "Could not load vendor detail."));
    }
  };
  const edit = (vendor) => {
    setEditing(vendor);
    setForm(toForm(vendor));
    setSelected(null);
    setFormOpen(true);
  };
  const startCreate = () => {
    setEditing(null);
    setForm(freshVendor());
    setSelected(null);
    setFormOpen(true);
  };
  const closeForm = () => {
    setEditing(null);
    setForm(freshVendor());
    setFormOpen(false);
  };
  const save = async (e) => {
    e.preventDefault();
    if (!canManage) return toast.error("You do not have permission to manage vendor masters.");
    if (!form.name.trim()) return toast.error("Vendor name is required");
    try {
      if (editing) await api.put(`/org/vendors/${editing.id}`, payload(form));
      else await api.post("/org/vendors", payload(form));
      toast.success(editing ? "Vendor saved" : "Vendor created");
      closeForm();
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };
  const deactivate = async (vendor) => {
    if (!confirmAction("Deactivate this vendor? Historical trip assignments will remain readable.")) return;
    try {
      await api.post(`/org/vendors/${vendor.id}/deactivate`);
      toast.success("Vendor deactivated");
      await load();
      if (selected?.id === vendor.id) await openDetail(vendor);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const vendorForm = formOpen && createPortal(
    <div className="summit-form-overlay">
      <form onSubmit={save} className="summit-form summit-form--sheet max-w-5xl">
        <SummitFormHeader eyebrow={editing ? "Update partner" : "New partner"} title={editing ? "Edit vendor" : "Create vendor"} description="Store reusable organization vendor details, private contacts, and charge references." onClose={closeForm} />
        <div className="summit-form__body">
          <SummitFormSection title="Vendor details" description="Capture the service, location, and primary point of contact.">
          <div className="grid gap-3 md:grid-cols-4">
            <Field label="Category" required><select className={inputCls} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>{categories.map((item) => <option key={item}>{item}</option>)}</select></Field>
            <Field label="Name" required><input className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
            <Field label="Region"><input className={inputCls} value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} /></Field>
            <Field label="Service types"><input className={inputCls} value={form.service_types} onChange={(e) => setForm({ ...form, service_types: e.target.value })} /></Field>
            <Field label="Website"><input className={inputCls} value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} /></Field>
            <Field label="Address"><input className={inputCls} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></Field>
            <Field label="Latitude"><input type="number" step="any" className={inputCls} value={form.latitude} onChange={(e) => setForm({ ...form, latitude: e.target.value })} /></Field>
            <Field label="Longitude"><input type="number" step="any" className={inputCls} value={form.longitude} onChange={(e) => setForm({ ...form, longitude: e.target.value })} /></Field>
            <Field label="Contact name"><input className={inputCls} value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} /></Field>
            <Field label="Contact phone"><input className={inputCls} value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} /></Field>
            <Field label="Contact email"><input className={inputCls} value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} /></Field>
            <Field label="Capacity"><input type="number" min="0" className={inputCls} value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} /></Field>
            <Field label="Rating"><input type="number" min="0" max="5" step="0.1" className={inputCls} value={form.rating} onChange={(e) => setForm({ ...form, rating: e.target.value })} /></Field>
            <Field label="Charges"><input type="number" min="0" step="0.01" className={inputCls} value={form.charges} onChange={(e) => setForm({ ...form, charges: e.target.value })} /></Field>
            <Field label="Tags"><input className={inputCls} value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} /></Field>
            <label className="mt-6 flex min-h-10 items-center gap-2 text-sm font-semibold"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Active</label>
            <Field label="Details"><textarea className={inputCls + " min-h-20 md:col-span-4"} value={form.details} onChange={(e) => setForm({ ...form, details: e.target.value })} /></Field>
          </div>
          </SummitFormSection>
          <SummitFormSection title="Pricing and private notes" description="Keep commercial context available to your operations team.">
          <div className="grid gap-3 md:grid-cols-2">
            <Field label="Pricing notes"><textarea className={inputCls + " min-h-24"} value={form.pricing_notes} onChange={(e) => setForm({ ...form, pricing_notes: e.target.value })} /></Field>
            <Field label="Internal notes"><textarea className={inputCls + " min-h-24"} value={form.internal_notes} onChange={(e) => setForm({ ...form, internal_notes: e.target.value })} /></Field>
          </div>
          </SummitFormSection>
        </div>
        <SummitFormActions><Btn type="button" variant="secondary" onClick={closeForm}>Cancel</Btn><Btn type="submit"><Save className="h-4 w-4" /> Save vendor</Btn></SummitFormActions>
      </form>
    </div>,
    document.body
  );
  const vendorDetail = selected && createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-[#091C18]/70 p-0 backdrop-blur-sm sm:items-center sm:p-4" role="dialog" aria-modal="true" aria-label="Vendor details">
      <aside className="max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-t-2xl border border-[#E2DFD7] bg-[#FFFBF3] p-4 shadow-2xl sm:rounded-2xl">
        <div className="flex items-start justify-between gap-3 border-b border-[#E2DFD7] pb-4">
          <div className="flex min-w-0 items-start gap-3">
            <VendorCategoryBadge category={selected.category} />
            <div className="min-w-0">
              <h3 className="truncate text-xl font-black text-[#17211D]">{selected.name}</h3>
              <p className="text-sm capitalize text-slate-500">{selected.category} - {selected.region || "No region"}</p>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {selected.contact_phone && (
              <a href={phoneHref(selected.contact_phone)} className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-[#E2DFD7] bg-white text-[#2D7A65] hover:bg-[#F7F1E5]" aria-label={`Call ${selected.name}`} title={`Call ${selected.contact_phone}`}>
                <PhoneCall className="h-4 w-4" />
              </a>
            )}
            <button type="button" className="rounded-lg border border-[#E2DFD7] bg-white p-2 text-[#516259] hover:bg-[#F7F1E5]" onClick={() => setSelected(null)} aria-label="Close vendor details">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <p className="rounded-md bg-white p-3 text-sm"><span className="block text-xs font-black uppercase text-[#516259]">Address</span>{selected.address || "Not set"}</p>
          <p className="rounded-md bg-white p-3 text-sm"><span className="block text-xs font-black uppercase text-[#516259]">Services</span>{csv(selected.service_types) || "Not set"}</p>
          <p className="rounded-md bg-white p-3 text-sm"><span className="block text-xs font-black uppercase text-[#516259]">Contact</span>{[selected.contact_name, selected.contact_phone, selected.contact_email].filter(Boolean).join(" - ") || "Not set"}</p>
          <p className="rounded-md bg-white p-3 text-sm"><span className="block text-xs font-black uppercase text-[#516259]">Capacity</span>{selected.capacity ?? "Not set"}</p>
          <p className="rounded-md bg-white p-3 text-sm"><span className="block text-xs font-black uppercase text-[#516259]">Charges</span>{selected.charges ?? "Not set"}</p>
          <p className="rounded-md bg-white p-3 text-sm"><span className="block text-xs font-black uppercase text-[#516259]">Rating</span><RatingStars value={selected.rating} /></p>
        </div>
        <div className="mt-3 space-y-3">
          <p className="rounded-md bg-white p-3 text-sm"><span className="block text-xs font-black uppercase text-[#516259]">Details</span>{selected.details || "No service details saved."}</p>
          <p className="rounded-md bg-white p-3 text-sm"><span className="block text-xs font-black uppercase text-[#516259]">Pricing</span>{selected.pricing_notes || "Not set"}</p>
          <p className="rounded-md bg-[#F7F1E5] p-3 text-sm"><span className="block text-xs font-black uppercase text-[#516259]">Internal</span>{selected.internal_notes || "No internal notes."}</p>
          <div>
            <p className="mb-2 text-xs font-black uppercase text-[#516259]">Trip assignments</p>
            {(selected.assignments || []).length === 0 ? <p className="rounded-md border border-dashed border-[#D8D2C4] p-3 text-sm text-slate-500">No trip assignments yet.</p> : (
              <div className="space-y-2">{selected.assignments.map((assignment) => (
                <div key={assignment.id} className="rounded-md border border-[#E2DFD7] bg-white p-2 text-sm">
                  <p className="font-semibold">{assignment.assignment_type || "Assignment"} - {assignment.status}</p>
                  <p className="text-xs text-slate-500">{assignment.start_date || "No start"} to {assignment.end_date || "No end"}</p>
                </div>
              ))}</div>
            )}
          </div>
        </div>
      </aside>
    </div>,
    document.body
  );

  if (!canRead) return <PermissionDeniedState message="You do not have permission to view organization vendors." />;
  if (error) return <ErrorState message={error} onRetry={load} />;
  if (items === null) return <LoadingState label="Loading vendors..." />;

  const query = searchTerm.trim().toLowerCase();
  const addressQuery = filters.address.trim().toLowerCase();
  const minCharges = numericValue(filters.minCharges);
  const maxCharges = numericValue(filters.maxCharges);
  const minRating = numericValue(filters.minRating);
  const filteredItems = items.filter((vendor) => {
    const charges = numericValue(vendor.charges);
    const rating = numericValue(vendor.rating);
    if (query && !vendorSearchText(vendor).includes(query)) return false;
    if (addressQuery && !String(vendor.address || "").toLowerCase().includes(addressQuery)) return false;
    if (minCharges !== null && (charges === null || charges < minCharges)) return false;
    if (maxCharges !== null && (charges === null || charges > maxCharges)) return false;
    if (minRating !== null && (rating === null || rating < minRating)) return false;
    return true;
  });

  return (
    <div className="space-y-4" data-testid="vendor-master-system">
      {vendorForm}
      {vendorDetail}
      <section className="rounded-lg border border-[#E2DFD7] bg-white p-4">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 font-semibold"><SlidersHorizontal className="h-4 w-4 text-[#2D7A65]" /> Vendor filters</div>
          {canManage && (
            <Btn type="button" onClick={startCreate}>
              <Plus className="h-4 w-4" /> Create vendor
            </Btn>
          )}
        </div>
        <div className="grid gap-3 md:grid-cols-4 lg:grid-cols-6">
          <div className="md:col-span-2">
            <Field label="Search all card fields"><input className={inputCls} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Name, address, region, service, charges, rating..." /></Field>
          </div>
          <Field label="Category"><select className={inputCls} value={filters.category} onChange={(e) => setFilters({ ...filters, category: e.target.value })}><option value="">All categories</option>{categories.map((item) => <option key={item}>{item}</option>)}</select></Field>
          <Field label="Region"><input className={inputCls} value={filters.region} onChange={(e) => setFilters({ ...filters, region: e.target.value })} /></Field>
          <Field label="Status"><select className={inputCls} value={filters.active} onChange={(e) => setFilters({ ...filters, active: e.target.value })}><option value="true">Active</option><option value="false">Inactive</option><option value="all">All</option></select></Field>
          <div className="flex items-end gap-2">
            <Btn type="button" variant="secondary" onClick={() => { setSearchTerm(""); setFilters({ category: "", region: "", active: "true", address: "", minCharges: "", maxCharges: "", minRating: "" }); }}>Clear</Btn>
          </div>
        </div>
      </section>

      {items.length === 0 ? <EmptyState title="No vendors found" description="Create reusable organization vendors, then assign them to trips." /> : (
        filteredItems.length === 0 ? <EmptyState title="No matching vendors" description="Adjust the search or filters to see more vendor cards." /> : (
        <div className="grid gap-3">
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {filteredItems.map((vendor) => (
              <article
                key={vendor.id}
                role="button"
                tabIndex={0}
                onClick={() => openDetail(vendor)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    openDetail(vendor);
                  }
                }}
                className={`group cursor-pointer overflow-hidden rounded-lg border p-4 transition hover:-translate-y-0.5 hover:shadow-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2D7A65] ${vendor.active ? "border-[#E2DFD7] bg-white" : "border-slate-200 bg-slate-50 opacity-80"}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex min-w-0 items-start gap-3">
                    <VendorCategoryBadge category={vendor.category} />
                    <div className="min-w-0">
                      <h3 className="truncate font-semibold text-[#17211D]">{vendor.name}</h3>
                      <p className="text-sm capitalize text-slate-500">{vendor.category} - {vendor.region || "No region"}</p>
                    </div>
                  </div>
                  <span className={`rounded-full px-2 py-1 text-xs font-bold ${vendor.active ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-700"}`}>{vendor.active ? "Active" : "Inactive"}</span>
                </div>
                <div className="mt-3 grid gap-2 rounded-md border border-[#E2DFD7] bg-[#FFFBF3] p-3 text-sm">
                  <p className="text-slate-600">{vendor.address || "No address saved."}</p>
                  <div className="flex flex-wrap items-center gap-3">
                    <RatingStars value={vendor.rating} />
                    <span className="text-xs font-semibold text-[#2D7A65]">Charges: {vendor.charges ?? "Not set"}</span>
                  </div>
                  {csv(vendor.service_types) && <p className="text-xs text-slate-500">Services: {csv(vendor.service_types)}</p>}
                </div>
                <p className="mt-2 text-sm text-slate-600">{vendor.details || "No service details saved."}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {vendor.contact_phone && (
                    <a href={phoneHref(vendor.contact_phone)} className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-[#E2DFD7] bg-white text-[#2D7A65] shadow-sm hover:bg-[#F7F1E5]" onClick={(e) => e.stopPropagation()} aria-label={`Call ${vendor.name}`} title={`Call ${vendor.contact_phone}`}>
                      <PhoneCall className="h-4 w-4" />
                    </a>
                  )}
                  {canManage && <Btn type="button" variant="secondary" onClick={(e) => { e.stopPropagation(); edit(vendor); }}>Edit</Btn>}
                  {canManage && vendor.active && <Btn type="button" variant="secondary" onClick={(e) => { e.stopPropagation(); deactivate(vendor); }}>Deactivate</Btn>}
                </div>
              </article>
            ))}
          </div>
        </div>
        )
      )}
    </div>
  );
}
